import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Camera,
  Save,
  UserCircle,
  Mail,
  Copy,
  Shield,
  LifeBuoy,
  Globe,
  Phone,
  ChevronRight,
  Loader2,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function CoachAccount() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [showPhone, setShowPhone] = useState(false);
  const [pictureData, setPictureData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [loadingContact, setLoadingContact] = useState(true);

  const [newEmail, setNewEmail] = useState("");
  const [requestingEmail, setRequestingEmail] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const { data } = await api.get("/profile/me");
        setPhone(data.phone || "");
        setShowPhone(Boolean(data.show_phone));
      } catch {
        toast.error("Impossible de charger vos coordonnées");
      } finally {
        setLoadingContact(false);
      }
    };
    fetchContact();
  }, []);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file || file.size > 5 * 1024 * 1024) {
      toast.error("Image max 5 Mo");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setPictureData(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    setSavingProfile(true);
    try {
      const payload = { name: name.trim() };
      if (pictureData) payload.picture_data = pictureData;
      await api.put("/auth/update-profile", payload);
      await refreshUser();
      setPictureData(null);
      toast.success("Profil mis à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      await api.put("/profile/me", {
        phone: phone.trim(),
        show_phone: showPhone,
      });
      toast.success("Coordonnées enregistrées");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingContact(false);
    }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setRequestingEmail(true);
    try {
      const { data } = await api.post("/auth/request-email-change", {
        new_email: newEmail.trim(),
      });
      toast.success(data.message || "Email de confirmation envoyé");
      setNewEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setRequestingEmail(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Mot de passe modifié");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setChangingPw(false);
    }
  };

  const copyCoachCode = () => {
    if (user?.coach_code) {
      navigator.clipboard.writeText(user.coach_code);
      toast.success("Code coach copié");
    }
  };

  const avatarSrc =
    preview ||
    (user?.picture
      ? user.picture.startsWith("http")
        ? user.picture
        : `${API_BASE}${user.picture}`
      : null);

  const quickLinks = [
    {
      label: "Profil public",
      description: "Pseudo, bio, visibilité",
      icon: Globe,
      path: "/coach/profile",
      color: "text-blue-600 bg-blue-50",
    },
    {
      label: "Sécurité",
      description: "Double authentification",
      icon: Shield,
      path: "/coach/security",
      color: "text-green-600 bg-green-50",
    },
    {
      label: "Support",
      description: "Aide et contact",
      icon: LifeBuoy,
      path: "/coach/support",
      color: "text-purple-600 bg-purple-50",
    },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-start gap-4 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
          <UserCircle className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
            Mon compte
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Gérez vos informations personnelles et la sécurité de votre espace coach
          </p>
        </div>
      </div>

      {user?.coach_code && (
        <Card className="border-blue-100 bg-blue-50/40 mb-6">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                Code coach
              </p>
              <p className="text-lg font-mono font-bold text-slate-900 mt-0.5">
                {user.coach_code}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Partagez ce code à vos élèves pour qu&apos;ils vous rejoignent
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copyCoachCode}
              className="rounded-full shrink-0"
            >
              <Copy className="w-4 h-4 mr-2" /> Copier
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Informations personnelles</h2>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={avatarSrc} />
                  <AvatarFallback className="bg-blue-100 text-blue-600 text-xl">
                    {name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhoto}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-full"
                >
                  <Camera className="w-4 h-4 mr-2" /> Photo
                </Button>
              </div>
              <div>
                <Label>Nom affiché</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 h-11"
                  placeholder="Votre nom"
                />
              </div>
              <Button
                type="submit"
                disabled={savingProfile}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-500" />
              Coordonnées
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              Votre téléphone peut être affiché sur votre profil public si vous l&apos;activez
            </p>
            {loadingContact ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : (
              <form onSubmit={saveContact} className="space-y-4">
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1.5 h-11"
                    placeholder="+32 470 00 00 00"
                  />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Afficher sur le profil public
                    </p>
                    <p className="text-xs text-slate-400">
                      Visible par les visiteurs de votre page coach
                    </p>
                  </div>
                  <Switch checked={showPhone} onCheckedChange={setShowPhone} />
                </div>
                <Button
                  type="submit"
                  disabled={savingContact}
                  variant="outline"
                  className="rounded-full"
                >
                  {savingContact ? "Enregistrement..." : "Enregistrer les coordonnées"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-500" />
              Adresse email
            </h2>
            <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 mb-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                Email actuel
              </p>
              <p className="text-sm font-medium text-slate-900 mt-1">{user?.email}</p>
            </div>
            <form onSubmit={requestEmailChange} className="space-y-4">
              <div>
                <Label>Nouvelle adresse email</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="mt-1.5 h-11"
                  placeholder="nouveau@email.com"
                />
              </div>
              <p className="text-xs text-slate-400">
                Un lien de confirmation sera envoyé à la nouvelle adresse
              </p>
              <Button
                type="submit"
                disabled={requestingEmail || !newEmail.trim()}
                variant="outline"
                className="rounded-full"
              >
                {requestingEmail ? "Envoi..." : "Demander le changement"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Mot de passe</h2>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <Label>Mot de passe actuel</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1.5 h-11"
                  autoComplete="current-password"
                />
              </div>
              <div>
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  className="mt-1.5 h-11"
                  autoComplete="new-password"
                />
              </div>
              <Button
                type="submit"
                disabled={changingPw || !currentPassword || !newPassword}
                variant="outline"
                className="rounded-full"
              >
                {changingPw ? "Modification..." : "Changer le mot de passe"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-100 mt-6">
        <CardContent className="p-6">
          <h2 className="font-semibold text-slate-900 mb-4">Accès rapides</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {quickLinks.map((link) => (
              <button
                key={link.path}
                type="button"
                onClick={() => navigate(link.path)}
                className="flex items-center gap-3 p-4 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-slate-50 transition-colors text-left group"
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${link.color}`}
                >
                  <link.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{link.label}</p>
                  <p className="text-xs text-slate-400 truncate">{link.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}